import React from 'react';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import HighlightText from '../components/HighlightText';

const { FiCheckCircle, FiClock, FiRefreshCw, FiAward, FiUsers, FiMapPin, FiPhone, FiStar, FiShield, FiTarget, FiTrendingUp } = FiIcons;

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About MyHomeTutors Lucknow',
  description: 'Learn about MyHomeTutors — Lucknow\'s trusted home tutoring platform since 2009. 500+ verified tutors, 10,000+ students served.',
  url: 'https://myhometutors.org/about',
  publisher: {
    '@type': 'Organization',
    name: 'MyHomeTutors',
    telephone: '+919415905584',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'H.N 1/358, Vastu Khand, Gomti Nagar',
      addressLocality: 'Lucknow',
      addressRegion: 'Uttar Pradesh',
      postalCode: '226010',
      addressCountry: 'IN',
    },
  },
};

const stats = [
  { value: '15+', label: 'Years of Trust', icon: FiClock, color: 'bg-blue-50 text-blue-600' },
  { value: '500+', label: 'Verified Tutors', icon: FiUsers, color: 'bg-green-50 text-green-600' },
  { value: '10,000+', label: 'Happy Students', icon: FiStar, color: 'bg-yellow-50 text-yellow-600' },
  { value: '27+', label: 'Areas Covered', icon: FiMapPin, color: 'bg-purple-50 text-purple-600' },
];

const values = [
  { icon: FiShield, title: 'Safety First', desc: 'Every tutor undergoes strict background verification before being listed on our platform.' },
  { icon: FiTarget, title: 'Personalized Learning', desc: 'We believe every child learns differently. Our tutors adapt to each student\'s pace and style.' },
  { icon: FiTrendingUp, title: 'Result Oriented', desc: 'Our tutors are committed to measurable improvement in grades and conceptual understanding.' },
  { icon: FiRefreshCw, title: 'Hassle-Free Replacement', desc: 'Not satisfied? We replace your tutor at no extra cost — your satisfaction is our priority.' },
  { icon: FiCheckCircle, title: 'Quality Assurance', desc: 'Regular feedback loops and performance tracking ensure consistent teaching quality.' },
  { icon: FiAward, title: 'Expert Educators', desc: 'From B.Ed holders to IIT graduates — our tutors are qualified subject matter experts.' },
];

const team = [
  { name: 'Academic Team', desc: 'Experienced educators who vet every tutor\'s subject expertise and teaching methodology.', img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=400&q=80' },
  { name: 'Support Team', desc: '24/7 support staff ready to match you with the right tutor and resolve any concerns.', img: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=400&q=80' },
  { name: 'Verification Team', desc: 'Dedicated team conducting background checks and qualification verification for all tutors.', img: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=400&q=80' },
];

const AboutPage = () => (
  <div className="min-h-screen bg-white">
    <Helmet>
      <title>About Us | MyHomeTutors Lucknow – Trusted Since 2009</title>
      <meta name="description" content="Learn about MyHomeTutors — Lucknow's trusted home tutoring platform since 2009. 500+ verified tutors, 10,000+ students, 27+ areas covered across Lucknow." />
      <link rel="canonical" href="https://myhometutors.org/about/" />
      <meta property="og:title" content="About Us | MyHomeTutors Lucknow – Trusted Since 2009" />
      <meta property="og:description" content="Learn about MyHomeTutors — Lucknow's trusted home tutoring platform since 2009." />
      <meta property="og:url" content="https://myhometutors.org/about" />
      <meta property="og:type" content="website" />
    </Helmet>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

    {/* Hero */}
    <section className="bg-gradient-to-br from-[#fff5f5] via-[#fff8f8] to-[#ffeaea] py-16 lg:py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 bg-white text-primary text-xs font-bold px-4 py-2 rounded-full border border-red-100 shadow-sm uppercase tracking-widest">
              <FiAward className="w-3.5 h-3.5" /> Trusted Since 2009
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-[1.15] tracking-tight">
              Lucknow's Most <HighlightText>Trusted</HighlightText> Home Tutoring Platform
            </h1>
            <p className="text-lg text-gray-600 font-medium leading-relaxed">
              Since 2009, MyHomeTutors has been connecting students across Lucknow with verified, expert home tutors — delivering personalized education right at your doorstep.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="tel:+919415905584" className="flex items-center justify-center gap-2 bg-primary text-white px-7 py-3.5 rounded-xl font-bold hover:bg-primary-hover transition-all shadow-lg tracking-wide">
                <FiPhone className="w-4 h-4" /> Call Us Now
              </a>
              <a href="/#tutors" className="flex items-center justify-center gap-2 bg-white text-primary border-2 border-primary px-7 py-3.5 rounded-xl font-bold hover:bg-red-50 transition-all shadow-sm tracking-wide">
                <FiUsers className="w-4 h-4" /> Meet Our Tutors
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=800&q=80" alt="MyHomeTutors team" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-3xl" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-4 z-10">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <FiCheckCircle className="text-green-600 w-6 h-6" />
              </div>
              <div>
                <p className="text-xl font-extrabold text-slate-900">10,000+</p>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Happy Students</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Stats */}
    <section className="py-14 bg-white border-b border-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-all">
              <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center mb-3`}>
                <s.icon className="w-6 h-6" />
              </div>
              <p className="text-3xl font-extrabold text-slate-900 mb-1">{s.value}</p>
              <p className="text-sm font-bold text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Our Story */}
    <section className="py-20 bg-[#fdf8f4]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
            Our <HighlightText>Story</HighlightText>
          </h2>
        </div>
        <div className="bg-white rounded-3xl p-8 md:p-12 border border-gray-100 shadow-sm space-y-5 text-gray-600 font-medium leading-relaxed text-base">
          <p>Founded in <strong className="text-slate-800">2009 in Lucknow</strong>, MyHomeTutors started with a simple mission: to make quality education accessible to every student — right at their home.</p>
          <p>We began as a small network of passionate educators connecting with students in Gomti Nagar and Indira Nagar. Over the years, we grew to cover <strong className="text-slate-800">27+ localities across Lucknow</strong>, building a community of 500+ verified tutors who share our commitment to excellence.</p>
          <p>Today, MyHomeTutors is Lucknow's most trusted home tutoring platform — helping students from <strong className="text-slate-800">Nursery to Class 12, CBSE, ICSE, IIT-JEE, and NEET</strong> preparation. Our tutors don't just teach subjects — they build confidence, discipline, and a love for learning.</p>
          <p>Every tutor on our platform goes through a rigorous <strong className="text-slate-800">background verification, qualification check, and teaching assessment</strong> before being listed. We believe parents deserve peace of mind, and students deserve the very best.</p>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-3">
            What We <HighlightText>Stand For</HighlightText>
          </h2>
          <p className="text-gray-500 font-medium max-w-xl mx-auto">Our core values guide everything we do — from tutor selection to student support.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((v, i) => (
            <div key={i} className="flex gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors">
                <v.icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-900 mb-1">{v.title}</h3>
                <p className="text-sm text-gray-500 font-medium leading-relaxed">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Teams */}
    <section className="py-20 bg-[#fdf8f4]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">
            The Team <HighlightText>Behind Us</HighlightText>
          </h2>
          <p className="text-gray-500 font-medium">Dedicated professionals working to make every tutoring experience exceptional.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {team.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all">
              <img src={t.img} alt={t.name} className="w-full h-44 object-cover" />
              <div className="p-6">
                <h3 className="font-extrabold text-slate-900 mb-2">{t.name}</h3>
                <p className="text-sm text-gray-500 font-medium leading-relaxed">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 bg-gradient-to-br from-primary to-orange-400">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Find Your Perfect Tutor?</h2>
        <p className="text-white/80 font-medium mb-8">Join 10,000+ students who trust MyHomeTutors for their education journey.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="tel:+919415905584" className="flex items-center justify-center gap-2 bg-white text-primary px-8 py-4 rounded-xl font-extrabold hover:bg-red-50 transition shadow-lg tracking-wide">
            <FiPhone className="w-5 h-5" /> +91 9415905584
          </a>
          <a href="/#subjects" className="flex items-center justify-center gap-2 bg-white/10 border-2 border-white text-white px-8 py-4 rounded-xl font-extrabold hover:bg-white/20 transition tracking-wide">
            Browse Subjects
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default AboutPage;